import { Fragment, useState, useMemo, useCallback } from 'react';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qTreatmentRecordsByPatient,
  qEncountersByPatient,
  qEncounterNotesPreviews,
  qInvoicesByPatient,
  type PatientTreatmentRecordRow,
  type PatientEncounterRow,
  type EncounterNotePreviewRow,
  type InvoiceRow,
} from '@helios/data-access';
import { TreatmentRecordDrawer } from './TreatmentRecordDrawer';
import { NotePreview } from './timeline-previews';
import { TreatmentRecordQuickToolbarHost } from './TreatmentRecordQuickToolbarHost';

// ─── Helpers ───

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(cents: number): string {
  return `₱${(cents / 100).toFixed(2)}`;
}

// ─── Props ───

interface TreatmentRecordViewProps {
  patientId: string;
  addRecordRef?: React.MutableRefObject<(() => void) | null>;
}

// ─── Component ───

export function TreatmentRecordView({ patientId, addRecordRef }: TreatmentRecordViewProps) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  const { data: records = [] } = usePowerSyncQuery<PatientTreatmentRecordRow>(
    qTreatmentRecordsByPatient(patientId),
  );
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncountersByPatient(patientId),
  );
  const { data: notesPreviews = [] } = usePowerSyncQuery<EncounterNotePreviewRow>(
    qEncounterNotesPreviews(patientId),
  );
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(qInvoicesByPatient(patientId));

  // Group all notes by their encounter so each treatment row can show the
  // full notes recorded on its visit note (combined, in chronological order).
  const notesByEncounter = useMemo(() => {
    const map: Record<string, EncounterNotePreviewRow[]> = {};
    for (const n of notesPreviews) {
      if (!map[n.encounter_id]) map[n.encounter_id] = [];
      map[n.encounter_id].push(n);
    }
    return map;
  }, [notesPreviews]);

  // Invoice → encounter fallback: many treatment records are auto-created
  // from invoice items and have `invoice_id` but no direct `encounter_id`,
  // so resolve through the invoice to reach the visit's notes.
  const invoiceToEncounter = useMemo(() => {
    const map: Record<string, string> = {};
    for (const inv of invoiceRows) {
      if (inv.encounter_id) map[inv.id] = inv.encounter_id;
    }
    return map;
  }, [invoiceRows]);

  // Date → encounters fallback: manually added treatment records often have
  // neither encounter_id nor invoice_id. Fall back to any encounter(s) on
  // the same day so their visit-note content can still surface.
  const encountersByDate = useMemo(() => {
    const map: Record<string, PatientEncounterRow[]> = {};
    for (const e of encounterRows) {
      const d = (e.started_at ?? e.created_at ?? '').slice(0, 10);
      if (!d) continue;
      if (!map[d]) map[d] = [];
      map[d].push(e);
    }
    return map;
  }, [encounterRows]);

  // Collect every encounter id a treatment record could be tied to. When
  // the record carries no direct link, the date fallback may yield multiple
  // encounters — we surface notes from all of them.
  const resolveEncounterIds = useCallback(
    (r: PatientTreatmentRecordRow): string[] => {
      if (r.encounter_id) return [r.encounter_id];
      if (r.invoice_id && invoiceToEncounter[r.invoice_id]) {
        return [invoiceToEncounter[r.invoice_id]];
      }
      const sameDay = encountersByDate[r.date];
      if (sameDay && sameDay.length > 0) return sameDay.map((e) => e.id);
      return [];
    },
    [invoiceToEncounter, encountersByDate],
  );

  // Group records by date and surface dates that have notes / invoices but
  // no procedures yet, so quick-toolbar entries (PRD.182) immediately show
  // up here instead of being hidden until a procedure is added.
  const recordsByDate = useMemo(() => {
    // Start with dates that have treatment records, preserving the
    // already-sorted (date DESC, created_at DESC) order from the query.
    const groups: { date: string; rows: PatientTreatmentRecordRow[] }[] = [];
    const seenDates = new Set<string>();
    for (const r of records) {
      const last = groups[groups.length - 1];
      if (last && last.date === r.date) {
        last.rows.push(r);
      } else {
        groups.push({ date: r.date, rows: [r] });
      }
      seenDates.add(r.date);
    }

    // Collect extra dates from encounters that have notes or invoices
    // attached, so a day with only documentation still appears.
    const extraDates = new Set<string>();
    const noteEncounterIds = new Set(notesPreviews.map((n) => n.encounter_id));
    const invoiceEncounterIds = new Set(
      invoiceRows.filter((i) => i.encounter_id).map((i) => i.encounter_id as string),
    );
    for (const e of encounterRows) {
      const d = (e.started_at ?? e.created_at ?? '').slice(0, 10);
      if (!d || seenDates.has(d)) continue;
      if (noteEncounterIds.has(e.id) || invoiceEncounterIds.has(e.id)) {
        extraDates.add(d);
      }
    }
    for (const d of extraDates) groups.push({ date: d, rows: [] });

    // Final sort: newest first.
    groups.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return groups;
  }, [records, notesPreviews, invoiceRows, encounterRows]);

  const collectDayNotes = useCallback(
    (date: string, rows: PatientTreatmentRecordRow[]): EncounterNotePreviewRow[] => {
      const seen = new Set<string>();
      const out: EncounterNotePreviewRow[] = [];
      // Resolve encounter ids from treatment records in the group.
      for (const r of rows) {
        for (const eid of resolveEncounterIds(r)) {
          for (const n of notesByEncounter[eid] ?? []) {
            const key = n.created_at ?? `${n.encounter_id}:${n.title}:${n.body}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(n);
          }
        }
      }
      // Also include notes for any encounter on this date (covers note-only
      // days and notes the date-fallback in resolveEncounterIds would miss).
      for (const e of encountersByDate[date] ?? []) {
        for (const n of notesByEncounter[e.id] ?? []) {
          const key = n.created_at ?? `${n.encounter_id}:${n.title}:${n.body}`;
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(n);
        }
      }
      return out;
    },
    [notesByEncounter, resolveEncounterIds, encountersByDate],
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<PatientTreatmentRecordRow | null>(null);

  // Expose add-record trigger to parent
  if (addRecordRef) {
    addRecordRef.current = () => {
      setEditRecord(null);
      setDrawerOpen(true);
    };
  }

  // Totals
  const totals = useMemo(() => {
    let charged = 0;
    let paid = 0;
    for (const r of records) {
      charged += r.amount_charged_cents ?? 0;
      paid += r.amount_paid_cents ?? 0;
    }
    return { charged, paid, balance: charged - paid };
  }, [records]);

  // Encounter options for drawer
  const encounterOptions = useMemo(
    () =>
      encounterRows.map((e) => ({
        id: e.id,
        label: `${e.type ?? 'Encounter'} — ${e.started_at ? new Date(e.started_at).toLocaleDateString() : 'No date'}`,
      })),
    [encounterRows],
  );

  const handleSave = useCallback(
    async (data: {
      date: string;
      tooth_nos: string;
      procedure_name: string;
      amount_charged_cents: number;
      amount_paid_cents: number;
      next_appointment_at: string;
      notes: string;
      encounter_id: string;
    }) => {
      if (!db) return;
      const now = new Date().toISOString();

      if (editRecord) {
        // Update
        await db.execute(
          `UPDATE patient_treatment_records SET date = ?, tooth_nos = ?, procedure_name = ?, amount_charged_cents = ?, amount_paid_cents = ?, next_appointment_at = ?, notes = ?, encounter_id = ?, updated_at = ? WHERE id = ?`,
          [
            data.date,
            data.tooth_nos || null,
            data.procedure_name,
            data.amount_charged_cents,
            data.amount_paid_cents,
            data.next_appointment_at || null,
            data.notes || null,
            data.encounter_id || null,
            now,
            editRecord.id,
          ],
        );
      } else {
        // Create
        const id = crypto.randomUUID();
        await db.execute(
          `INSERT INTO patient_treatment_records (id, org_id, clinic_id, patient_id, encounter_id, date, tooth_nos, procedure_name, amount_charged_cents, amount_paid_cents, next_appointment_at, notes, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id,
            orgId,
            null,
            patientId,
            data.encounter_id || null,
            data.date,
            data.tooth_nos || null,
            data.procedure_name,
            data.amount_charged_cents,
            data.amount_paid_cents,
            data.next_appointment_at || null,
            data.notes || null,
            now,
            now,
          ],
        );
      }
      setEditRecord(null);
    },
    [db, orgId, patientId, editRecord],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!db) return;
      await db.execute(`DELETE FROM patient_treatment_records WHERE id = ?`, [id]);
    },
    [db],
  );

  const thStyle = {
    padding: '10px 12px',
    textAlign: 'left' as const,
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semi)',
    color: 'var(--text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap' as const,
  };

  const tdStyle = {
    padding: '10px 12px',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--text)',
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap' as const,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      {/* PRD.182 — shared Treatment Record Quick Toolbar */}
      <TreatmentRecordQuickToolbarHost patientId={patientId} />

      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'auto',
          background: 'var(--bg-surface)',
        }}
      >
        {/* Header: title + totals + Add Record */}
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-5)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 'var(--sp-4)',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--text)',
              }}
            >
              Treatment Record
            </div>
            <div
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 2 }}
            >
              Chronological dental procedures and payments
            </div>
            {/* Inline totals */}
            <div
              style={{
                display: 'flex',
                gap: 'var(--sp-4)',
                marginTop: 8,
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <span>
                <span style={{ color: 'var(--text-muted)' }}>Total Charged: </span>
                <span style={{ fontWeight: 'var(--font-weight-semi)', color: 'var(--text)' }}>
                  {formatCurrency(totals.charged)}
                </span>
              </span>
              <span>
                <span style={{ color: 'var(--text-muted)' }}>Total Paid: </span>
                <span style={{ fontWeight: 'var(--font-weight-semi)', color: '#16a34a' }}>
                  {formatCurrency(totals.paid)}
                </span>
              </span>
              <span>
                <span style={{ color: 'var(--text-muted)' }}>Total Balance: </span>
                <span
                  style={{
                    fontWeight: 'var(--font-weight-semi)',
                    color:
                      totals.balance > 0
                        ? '#dc2626'
                        : totals.balance < 0
                          ? '#16a34a'
                          : 'var(--text)',
                  }}
                >
                  {formatCurrency(totals.balance)}
                </span>
              </span>
            </div>
          </div>
          {!addRecordRef && (
            <button
              onClick={() => {
                setEditRecord(null);
                setDrawerOpen(true);
              }}
              style={{
                height: 36,
                padding: '0 16px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: primary,
                color: '#fff',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
              }}
            >
              <NavIcon name="plus" /> Add Record
            </button>
          )}
        </div>

        {/* Table */}
        <div>
          {recordsByDate.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: 'var(--sp-8)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              No treatment records yet. Click "+ Add Record" to create one.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Tooth No/s</th>
                  <th style={thStyle}>Procedure</th>
                  <th style={{ ...thStyle, minWidth: 240 }}>Notes</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Charged</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Paid</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Balance</th>
                  <th style={thStyle}>Next Appt</th>
                  <th style={{ ...thStyle, width: 70 }}></th>
                </tr>
              </thead>
              <tbody>
                {recordsByDate.map(({ date, rows }) => {
                  const dayNotes = collectDayNotes(date, rows);
                  return (
                    <Fragment key={date}>
                      {dayNotes.length > 0 && (
                        <tr style={{ background: 'var(--bg-surface-2, #f9fafb)' }}>
                          <td
                            style={{
                              ...tdStyle,
                              verticalAlign: 'top',
                              fontWeight: 'var(--font-weight-medium)',
                            }}
                          >
                            {formatDate(date)}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--text-placeholder)' }}>—</td>
                          <td style={{ ...tdStyle, color: 'var(--text-placeholder)' }}>—</td>
                          <td
                            style={{
                              ...tdStyle,
                              whiteSpace: 'normal',
                              verticalAlign: 'top',
                              minWidth: 240,
                              maxWidth: 420,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--sp-3)',
                              }}
                            >
                              {dayNotes.map((n, ni) => (
                                <NotePreview key={n.created_at ?? `${ni}`} note={n} marker="dot" />
                              ))}
                            </div>
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: 'right',
                              color: 'var(--text-placeholder)',
                            }}
                          >
                            —
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: 'right',
                              color: 'var(--text-placeholder)',
                            }}
                          >
                            —
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: 'right',
                              color: 'var(--text-placeholder)',
                            }}
                          >
                            —
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--text-placeholder)' }}>—</td>
                          <td style={tdStyle}></td>
                        </tr>
                      )}
                      {rows.map((r) => {
                        const balance = (r.amount_charged_cents ?? 0) - (r.amount_paid_cents ?? 0);
                        return (
                          <tr key={r.id}>
                            <td style={tdStyle}>{formatDate(r.date)}</td>
                            <td style={tdStyle}>{r.tooth_nos || '—'}</td>
                            <td style={tdStyle}>{r.procedure_name}</td>
                            <td style={tdStyle} />
                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                              {formatCurrency(r.amount_charged_cents ?? 0)}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: '#16a34a' }}>
                              {formatCurrency(r.amount_paid_cents ?? 0)}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                textAlign: 'right',
                                fontWeight: 'var(--font-weight-semi)',
                                color:
                                  balance > 0 ? '#dc2626' : balance < 0 ? '#16a34a' : 'var(--text)',
                              }}
                            >
                              {formatCurrency(balance)}
                            </td>
                            <td style={tdStyle}>
                              {r.next_appointment_at
                                ? formatDate(r.next_appointment_at.split('T')[0])
                                : '—'}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                display: 'flex',
                                gap: 4,
                                justifyContent: 'flex-end',
                              }}
                            >
                              <button
                                onClick={() => {
                                  setEditRecord(r);
                                  setDrawerOpen(true);
                                }}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 'var(--radius-sm)',
                                  border: 'none',
                                  background: 'transparent',
                                  cursor: 'pointer',
                                  color: 'var(--text-muted)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                }}
                              >
                                <NavIcon name="edit" />
                              </button>
                              <button
                                onClick={() => handleDelete(r.id)}
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 'var(--radius-sm)',
                                  border: 'none',
                                  background: 'transparent',
                                  cursor: 'pointer',
                                  color: 'var(--text-placeholder)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                }}
                              >
                                <NavIcon name="x" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <TreatmentRecordDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setEditRecord(null);
          }}
          primary={primary}
          editRecord={editRecord}
          onSave={handleSave}
          encounters={encounterOptions}
        />
      </div>
    </div>
  );
}
